import { useState } from "react";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import type { TelecallerOutcome } from "../../voice/telecaller.types";

// ---------------------------------------------------------------------------
// OutcomeCard — what the AI wrote on the lead when the call wrapped up: the
// disposition, the summary, the facts it learned, the follow-up it scheduled
// (or why it could not). The fallback variant says the transcript was saved
// because the call ended before the AI could log.
// ---------------------------------------------------------------------------

const TONE: Record<string, string> = {
  interested: "#9ad7bd",
  callback: "#9ad7bd",
  wants_human: "#9ad7bd",
  not_interested: "#ffb27a",
  do_not_call: "#ff9c9c",
  wrong_number: "#ffb27a",
  no_answer: "#d9d9d9",
  language_barrier: "#d9d9d9",
  incomplete: "#d9d9d9",
};

export default function OutcomeCard({ outcome, language, onOpenLead }: { outcome: TelecallerOutcome; language: "ml" | "en"; onOpenLead?: () => void }) {
  const ml = language === "ml";
  const tone = TONE[outcome.disposition] ?? "#d9d9d9";
  const suggestion = outcome.suggestion ?? null;
  // "Apply" rides the SAME mutation the lead drawer uses (stage-update), so
  // every gate — requirements, follow-up rules, role permissions — applies
  // and a refusal surfaces as the usual toast. Never automatic.
  const { updateStage } = useLeadMutations();
  const [applied, setApplied] = useState(false);
  const canApply = !!suggestion && !outcome.practice && !!outcome.lead?.id && !applied;
  const apply = () => {
    if (!suggestion || !outcome.lead?.id) return;
    updateStage.mutate(
      { id: outcome.lead.id, data: { stageId: suggestion.stageId, statusId: suggestion.statusId, ...(suggestion.subStatusId ? { subStatusId: suggestion.subStatusId } : {}) } },
      { onSuccess: () => setApplied(true) },
    );
  };
  const suggestionTone = suggestion?.kind === "rejection" ? "#ff9c9c" : suggestion?.kind === "qualifying" ? "#9ad7bd" : "#eaf5f1";
  return (
    <div className="xv-rec" style={{ display: "block" }}>
      <div className="xv-rec-top" style={{ alignItems: "flex-start" }}>
        <div className="xv-rec-meta" style={{ flex: 1, minWidth: 0 }}>
          <div className="xv-rec-title" style={{ color: tone }}>
            {outcome.label}
            {outcome.interest ? <span style={{ opacity: 0.6, fontWeight: 400 }}> · {outcome.interest}</span> : null}
          </div>
          <div className="xv-rec-sub">
            {outcome.practice
              ? ml
                ? "പ്രാക്ടീസ് — ലീഡിൽ ഒന്നും save ചെയ്തിട്ടില്ല. ഇതാണ് AI എഴുതുമായിരുന്ന note."
                : "Practice — nothing was saved on the lead. This is the note the AI would have written."
              : outcome.fallback
              ? ml
                ? "AI-ക്ക് wrap-up ചെയ്യാൻ കഴിഞ്ഞില്ല — സംഭാഷണം note ആയി save ചെയ്തു."
                : "The call ended before the AI could wrap up — the transcript was saved as a note."
              : ml
                ? "ലീഡിന്റെ notes-ൽ call note save ചെയ്തു."
                : "Call note saved on the lead."}
          </div>
        </div>
        {onOpenLead && (
          <button className="xv-pill" onClick={onOpenLead} style={{ height: 30, fontSize: 12 }}>
            {ml ? "ലീഡ് തുറക്കുക" : "Open lead"}
          </button>
        )}
      </div>
      {outcome.summary && <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{outcome.summary}</div>}
      {outcome.keyFacts && outcome.keyFacts.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12 }}>
          {outcome.keyFacts.map((f, i) => (
            <span key={i} style={{ padding: "3px 9px", borderRadius: 999, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
              {f}
            </span>
          ))}
        </div>
      )}
      {outcome.nextStep && (
        <div className="xv-rec-sub">
          <span style={{ opacity: 0.7 }}>{ml ? "അടുത്തത്: " : "Next: "}</span>
          {outcome.nextStep}
        </div>
      )}
      {suggestion && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            padding: "9px 12px", borderRadius: 12, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <span className="xv-rec-sub" style={{ flex: "none" }}>{outcome.practice ? (ml ? "മാറ്റുമായിരുന്നത്:" : "Would move to:") : ml ? "നിർദ്ദേശിക്കുന്ന status:" : "Suggested status:"}</span>
          <span style={{ fontSize: 13.5, fontWeight: 550, color: suggestionTone, flex: 1, minWidth: 0 }}>
            {suggestion.stageName} › {suggestion.statusName}
            {suggestion.subStatusName ? ` › ${suggestion.subStatusName}` : ""}
          </span>
          {!outcome.practice && (
            <button className="xv-pill is-primary" onClick={apply} disabled={!canApply || updateStage.isPending} style={{ height: 30, fontSize: 12 }}>
              {applied ? (ml ? "മാറ്റി ✓" : "Applied ✓") : updateStage.isPending ? "…" : ml ? "ഇതിലേക്ക് മാറ്റുക" : "Apply"}
            </button>
          )}
        </div>
      )}
      {outcome.followUp && (
        <div className="xv-rec-sub" style={{ color: outcome.followUp.created ? "#9ad7bd" : "#ffb27a" }}>
          {outcome.followUp.created
            ? outcome.practice
              ? ml
                ? `Follow-up set ചെയ്യുമായിരുന്നു: ${outcome.followUp.at}`
                : `Would have scheduled a follow-up for ${outcome.followUp.at}`
              : ml
                ? `Follow-up set: ${outcome.followUp.at}`
                : `Follow-up scheduled for ${outcome.followUp.at}`
            : ml
              ? `Follow-up set ചെയ്യാനായില്ല — ${outcome.followUp.reason}`
              : `Follow-up not scheduled — ${outcome.followUp.reason}`}
        </div>
      )}
    </div>
  );
}
